import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Download, Scale } from "lucide-react";
import { api, API } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { VerifyChip } from "../components/DataLabel";
import { StartAgain } from "../components/StartAgain";
import { FacilityPicker } from "../components/FacilityPicker";
import { SavedFaceOffs } from "../components/SavedFaceOffs";
import { acresPhrase } from "../lib/units";

const fmt = (kind, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (kind === "text") return String(v);
  if (kind === "usd") return `$${Math.round(v).toLocaleString()}`;
  if (kind === "usd2") return `$${Number(v).toFixed(2)}`;
  if (kind === "ksh") return `KSh ${Math.round(v).toLocaleString()}`;
  if (kind === "pct") return `${v}%`;
  if (kind === "years") return `${v} yrs`;
  if (kind === "acres") return `${v} ac`;
  if (kind === "index") return Number(v).toFixed(2);
  return Math.round(v).toLocaleString();
};

/** Two facilities held up against each other before anyone commits to building one. */
export default function CompareFacilities() {
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") === "counties" ? "counties" : "facilities";
  const facility = params.get("facility") || "branded_five";
  const a = params.get("a") || (mode === "counties" ? "Kisumu" : "community_court");
  const b = params.get("b") || (mode === "counties" ? "Nakuru" : "branded_five");
  const county = params.get("county") || "Kisumu";
  const funding = params.get("funding") || "ppp";
  const acres = (params.get("acres") || "").replace(/^\.$/, "");
  const [lib, setLib] = useState(null);
  const [counties, setCounties] = useState([]);
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(false);
  const [picking, setPicking] = useState("a");

  useSeo({
    title: "Compare Two Facilities — What To Build, Side By Side",
    description:
      "Hold two facilities up against each other before you build: land needed, build cost, trading income, indicative operating surplus, player visits and jobs, priced on one county's own wages and rents — with the money case and the reach case reported separately.",
  });

  const set = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    api.get("/facility-library").then((r) => setLib(r.data)).catch(() => setLib(null));
    api.get("/counties")
      .then((r) => setCounties((Array.isArray(r.data) ? r.data : r.data.counties || []).map((c) => c.name).filter(Boolean)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(
      mode === "counties"
        ? { facility, a, b, funding, ...(acres ? { acres } : {}) }
        : { a, b, county, funding, ...(acres ? { acres } : {}) },
    ).toString();
    api.get(`/${mode === "counties" ? "county-face-off" : "facility-face-off"}?${q}`)
      .then((r) => { setD(r.data); setFailed(false); })
      .catch(() => setFailed(true));
  }, [mode, facility, a, b, county, funding, acres]);

  const query = useMemo(
    () => new URLSearchParams(
      mode === "counties"
        ? { facility, a, b, funding, ...(acres ? { acres } : {}) }
        : { a, b, county, funding, ...(acres ? { acres } : {}) },
    ).toString(),
    [mode, facility, a, b, county, funding, acres],
  );

  const current = { mode, facility, a, b, county, funding, acres };

  const openSaved = (s) => {
    setParams(new URLSearchParams(
      s.mode === "counties"
        ? { mode: "counties", facility: s.facility, a: s.a, b: s.b, funding: s.funding, ...(s.acres ? { acres: s.acres } : {}) }
        : { a: s.a, b: s.b, county: s.county, funding: s.funding, ...(s.acres ? { acres: s.acres } : {}) },
    ), { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const switchMode = (next) => {
    const p = new URLSearchParams({ mode: next, funding, ...(acres ? { acres } : {}) });
    const isFacility = (id) => (lib?.facilities || []).some((f) => f.id === id);
    const isCounty = (name) => counties.includes(name);
    if (next === "counties") {
      const keep = isFacility(a) ? a : facility;
      p.set("facility", keep);
      const first = isCounty(county) ? county : "Kisumu";
      p.set("a", first);
      p.set("b", first === "Nakuru" ? "Kisumu" : "Nakuru");
    } else {
      const keep = isFacility(facility) ? facility : "branded_five";
      p.set("a", keep);
      p.set("b", keep === "branded_five" ? "seven_centre" : "branded_five");
      p.set("county", isCounty(a) ? a : county);
    }
    setParams(p, { replace: true });
  };

  const options = lib ? lib.facilities.filter((f) => f.id !== (picking === "a" ? b : a)) : [];
  const shareTitle = mode === "counties" ? "One Facility, Two Counties" : "Two Facilities, Side By Side";
  const select = "mt-2 w-full border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none";
  const label = "mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500";

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="compare-facilities-page">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <p className="overline text-ochre">Before you build</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d ? d.heading : shareTitle}</h1>
          {d ? <p className="body-copy mt-6 max-w-3xl">{d.intro}</p> : null}
          <p className="mono mt-8 flex max-w-3xl items-start gap-3 border-l-2 border-maasai bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
            <Scale size={16} className="mt-0.5 shrink-0 text-maasai" />
            <span><VerifyChip v="model" /> {d ? d.caveat : "Modelled on each county's own wage, rent and tariff data."}</span>
          </p>
        </Reveal>

        <section className="mt-12" data-testid="compare-controls">
          <div className="mb-8 flex flex-wrap gap-2" data-testid="compare-mode">
            {[["facilities", "Two facilities, one county"], ["counties", "One facility, two counties"]].map(([id, text]) => (
              <button key={id} type="button" onClick={() => switchMode(id)} data-testid={`compare-mode-${id}`}
                className={`mono border px-4 py-2.5 text-[0.6rem] uppercase tracking-[0.14em] transition-colors ${
                  mode === id ? "border-maasai bg-maasai text-white" : "border-white/15 text-zinc-300 hover:border-ochre hover:text-ochre"
                }`}>
                {text}
              </button>
            ))}
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {mode === "counties" ? (
              <>
                <label className="block">
                  <span className={label}>First county</span>
                  <select value={a} onChange={(e) => set("a", e.target.value)} data-testid="compare-county-a" className={select}>
                    {counties.filter((c) => c !== b).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className={label}>Second county</span>
                  <select value={b} onChange={(e) => set("b", e.target.value)} data-testid="compare-county-b" className={select}>
                    {counties.filter((c) => c !== a).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </>
            ) : (
              <label className="block">
                <span className={label}>County</span>
                <select value={county} onChange={(e) => set("county", e.target.value)} data-testid="compare-county" className={select}>
                  {counties.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            )}
            <label className="block">
              <span className={label}>Funding route</span>
              <select value={funding} onChange={(e) => set("funding", e.target.value)} data-testid="compare-funding" className={select}>
                {(lib?.funding || []).map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className={label}>Acres you hold (optional)</span>
              <input value={acres} inputMode="decimal" data-testid="compare-acres"
                onChange={(e) => set("acres", e.target.value.replace(/[^\d.]/g, "").replace(/^\.+/, ""))}
                placeholder="Leave blank to ignore land"
                className="mt-2 w-full border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none" />
            </label>
            <div className="flex items-end gap-3">
              <a href={`${API}/${mode === "counties" ? "county-face-off" : "facility-face-off"}.pdf?${query}`}
                target="_blank" rel="noreferrer" className="btn-primary" data-testid="compare-pdf">
                <Download size={14} /> Face-off PDF
              </a>
              <StartAgain label="Start again" to="/compare-facilities" testId="compare-start-again" />
            </div>
          </div>

          {mode === "counties" ? (
            <>
              <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="compare-facility-choice">
                <span className={label}>The facility being priced in both</span>
              </div>
              <div className="mt-4">
                <FacilityPicker facilities={lib?.facilities || []} value={facility}
                  onChange={(id) => set("facility", id)} testId="compare-facility-picker" />
              </div>
            </>
          ) : (
            <>
              <div className="mt-8 flex flex-wrap gap-2" data-testid="compare-side-toggle">
            {[["a", d?.a?.name || "First facility"], ["b", d?.b?.name || "Second facility"]].map(([side, name]) => (
              <button key={side} type="button" onClick={() => setPicking(side)}
                data-testid={`compare-pick-${side}`}
                className={`mono border px-3.5 py-2 text-left text-[0.6rem] uppercase tracking-[0.14em] transition-colors ${
                  picking === side ? "border-ochre bg-ochre text-black" : "border-white/15 text-zinc-300 hover:border-ochre hover:text-ochre"
                }`}>
                {side === "a" ? "Change the first" : "Change the second"} · {name}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <FacilityPicker facilities={options} value={picking === "a" ? a : b}
              onChange={(id) => set(picking, id)} testId="compare-facility-picker" />
          </div>
            </>
          )}
        </section>

        {failed ? (
          <p className="mt-10 max-w-3xl border border-maasai/40 bg-maasai/10 p-6 text-sm text-white"
            data-testid="compare-error">
            We could not price that comparison. Check the acreage you entered, or pick two different facilities.
          </p>
        ) : null}

        {d ? (
          <>
            {d.eligibility_note ? (
              <p className="mono mt-10 max-w-3xl border-l-2 border-maasai bg-[#1a0e0e] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-200"
                data-testid="compare-eligibility-note">
                {d.eligibility_note}
              </p>
            ) : null}

            {d.land_note ? (
              <p className="mono mt-10 max-w-3xl border-l-2 border-maasai bg-[#12100a] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-300"
                data-testid="compare-land-note">
                {d.land_note}
              </p>
            ) : null}

            <section className="mt-12 overflow-x-auto border border-zinc-900" data-testid="compare-table">
              <table className="w-full min-w-[860px] text-left">
                <thead>
                  <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                    {["", d.mode === "counties" ? d.a.county : d.a.name, d.mode === "counties" ? d.b.county : d.b.name, "Better"].map((h, i) => (
                      <th key={i} className="mono px-4 py-4 align-top text-[0.6rem] uppercase leading-relaxed tracking-[0.16em] text-zinc-300">
                        {h}
                        {i === 1 || i === 2 ? (
                          <span className="mt-2 block text-[0.5rem] text-zinc-500">
                            {d.mode === "counties"
                              ? ((i === 1 ? d.a : d.b).eligible ? "Qualifies" : "Does not qualify")
                              : (i === 1 ? d.a : d.b).tier_label}
                            {d.mode !== "counties" && !(i === 1 ? d.a : d.b).fits_the_land && d.acres ? " · does not fit your land" : ""}
                          </span>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((r) => (
                    <tr key={r.label} className="border-b border-zinc-900" data-testid={`compare-row-${r.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <td className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-400">{r.label}</td>
                      <td className={`mono px-4 py-3 text-xs ${r.better === "a" ? "text-ochre" : "text-zinc-300"}`}>{fmt(r.kind, r.a)}</td>
                      <td className={`mono px-4 py-3 text-xs ${r.better === "b" ? "text-ochre" : "text-zinc-300"}`}>{fmt(r.kind, r.b)}</td>
                      <td className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">
                        {r.better === "a" ? (d.mode === "counties" ? d.a.county : d.a.name)
                          : r.better === "b" ? (d.mode === "counties" ? d.b.county : d.b.name) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="mt-12 grid gap-px bg-zinc-800 lg:grid-cols-2" data-testid="compare-cases">
              {["money_case", "reach_case"].map((k) => (
                <div key={k} className="bg-[#0b0b0b] p-7 md:p-9" data-testid={`compare-${k.replace("_case", "")}`}>
                  <p className="eyebrow">{d[k].heading}</p>
                  <p className="display-3 mt-4">{d[k].text || "Level."}</p>
                  <p className="mt-5 text-sm leading-relaxed text-zinc-400">{d[k].note}</p>
                </div>
              ))}
            </section>

            <p className="mt-8 max-w-4xl border-l-2 border-ochre bg-[#0b0b0b] p-5 text-sm leading-relaxed text-zinc-300"
              data-testid="compare-trade-off">
              {d.trade_off}
            </p>

            {d.same_budget ? (
              <section className="mt-10 max-w-4xl border border-zinc-900 bg-[#0b0b0b] p-7 md:p-9"
                data-testid="compare-same-budget">
                <p className="eyebrow">{d.same_budget.heading}</p>
                <p className="mt-4 text-sm leading-relaxed text-zinc-300">{d.same_budget.text}</p>
                <div className="mono mt-6 grid gap-px bg-zinc-800 sm:grid-cols-3">
                  {[["The budget", `$${Math.round(d.same_budget.budget_usd).toLocaleString()}`],
                    [`One in ${d.same_budget.dearer_county}`, "1"],
                    [`In ${d.same_budget.cheaper_county}`, `${d.same_budget.multiple}`]].map(([k, v]) => (
                      <div key={k} className="bg-black px-5 py-4">
                        <span className="block text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">{k}</span>
                        <span className="mt-2 block text-sm text-white">{v}</span>
                      </div>
                    ))}
                </div>
              </section>
            ) : null}

            {d.ranking ? (
              <section className="mt-16" data-testid="compare-ranking">
                <p className="eyebrow">Across the country</p>
                <h2 className="display-3 mt-4">{d.ranking.heading}</h2>
                <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">
                  {d.ranking.intro} {d.ranking.facility} qualifies in {d.ranking.eligible_counties} of 47 counties.
                </p>
                <div className="mt-8 max-h-[520px] overflow-auto border border-zinc-900">
                  <table className="w-full min-w-[680px] text-left" data-testid="compare-ranking-table">
                    <thead className="sticky top-0 bg-[#0b0b0b]">
                      <tr className="border-b border-zinc-800">
                        {["#", "County", "Surplus share", "Yrs to match cost", "Demand", "Qualifies"].map((h) => (
                          <th key={h} className="mono px-4 py-3 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {d.ranking.rows.map((r) => (
                        <tr key={r.county}
                          className={`border-b border-zinc-900 ${r.picked ? "bg-[#161208]" : ""}`}
                          data-testid={`compare-rank-${r.county.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          <td className="mono px-4 py-3 text-xs text-zinc-500">{r.rank}</td>
                          <td className={`px-4 py-3 text-sm ${r.picked ? "text-ochre" : "text-white"}`}>{r.county}</td>
                          <td className="mono px-4 py-3 text-xs text-zinc-300">{r.roce_pct != null ? `${r.roce_pct}%` : "—"}</td>
                          <td className="mono px-4 py-3 text-xs text-zinc-300">{r.payback_years ? `${r.payback_years} yrs` : "—"}</td>
                          <td className="mono px-4 py-3 text-xs text-zinc-500">{r.demand_index}</td>
                          <td className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-400">
                            {r.eligible ? "Yes" : "No"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            {d.where ? (
              <section className="mt-16" data-testid="compare-where">
                <p className="eyebrow">Across the country</p>
                <h2 className="display-3 mt-4">{d.where.heading}</h2>
                <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">
                  <span className="text-white">{d.where.a_name}</span> shows the higher surplus share in {d.where.a_counties} counties,
                  {" "}<span className="text-white">{d.where.b_name}</span> in {d.where.b_counties}. {d.where.intro}
                </p>
                <div className="mt-8 overflow-x-auto border border-zinc-900">
                  <table className="w-full min-w-[720px] text-left" data-testid="compare-where-table">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                        {["County", `${d.where.a_name} surplus share`, `${d.where.b_name} surplus share`, "Gap", "Wins here"].map((h) => (
                          <th key={h} className="mono px-4 py-3 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {d.where.rows.slice(0, 12).map((r) => (
                        <tr key={r.county} className="border-b border-zinc-900" data-testid={`compare-where-${r.county.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          <td className="px-4 py-3 text-sm text-white">{r.county}</td>
                          <td className="mono px-4 py-3 text-xs text-zinc-300">{r.a_roce_pct != null ? `${r.a_roce_pct}%` : "—"}</td>
                          <td className="mono px-4 py-3 text-xs text-zinc-300">{r.b_roce_pct != null ? `${r.b_roce_pct}%` : "—"}</td>
                          <td className="mono px-4 py-3 text-xs text-zinc-500">{r.gap_pct}pts</td>
                          <td className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.14em] text-ochre">
                            {r.winner === "a" ? d.where.a_name : r.winner === "b" ? d.where.b_name : "Level"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mono mt-4 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                  The twelve counties where the gap is widest. All 47 are in the face-off PDF.
                </p>
              </section>
            ) : null}

            <div className="mono mt-14 flex flex-wrap gap-4 text-[0.6rem] uppercase tracking-[0.16em]">
              <Link to={`/facility-planner?facility=${d.mode === "counties" ? facility : a}&county=${d.mode === "counties" ? a : county}&funding=${funding}`} className="text-ochre hover:text-white" data-testid="compare-to-planner">
                Price {d.mode === "counties" ? d.facility.name : d.a.name} in full
              </Link>
              <Link to="/facilities" className="text-ochre hover:text-white" data-testid="compare-to-facilities">All nine facilities</Link>
              <Link to="/apply" className="text-ochre hover:text-white" data-testid="compare-to-apply">File a site</Link>
              {d.acres ? (
                <Link to={`/site-appraisal?county=${d.mode === "counties" ? d.a.county : county}&acres=${d.acres}`} className="text-ochre hover:text-white" data-testid="compare-to-appraisal">
                  Appraise {acresPhrase(d.acres)} in {d.mode === "counties" ? d.a.county : county} <ArrowUpRight size={11} />
                </Link>
              ) : null}
            </div>
          </>
        ) : null}

        <SavedFaceOffs current={current} onOpen={openSaved} />
      </div>
    </div>
  );
}
